import type { ChannelConfig } from './channel';
import type { VideoConfig } from './video';

/**
 * Пресеты канала (п. 10 ТЗ). Значения предварительные: после замеров реальных
 * задержек телеопа (открытый вопрос 3 п. 17) пресет «LTE хороший» должен стать
 * пресетом по умолчанию с фактическими цифрами.
 */
export interface LinkPreset {
  id: string;
  name: string;
  hint: string;
  command: ChannelConfig;
  telemetry: ChannelConfig;
  video: VideoConfig;
}

const ch = (delay: number, jitter: number, loss: number, duplicate = 0): ChannelConfig => ({
  delay_ms: delay,
  jitter_ms: jitter,
  loss,
  duplicate,
  down: false,
});

export const LINK_PRESETS: LinkPreset[] = [
  {
    id: 'ideal',
    name: 'Идеал',
    hint: 'Отладочный режим. Так не бывает.',
    command: ch(0, 0, 0),
    telemetry: ch(0, 0, 0),
    video: { ...ch(0, 0, 0), fps: 30, scale: 1, freeze_per_min: 0, freeze_ms: [0, 0], artifacts: 0 },
  },
  {
    id: 'wifi',
    name: 'Wi-Fi склада',
    hint: 'Низкая задержка, редкие короткие фризы у стеллажей.',
    command: ch(25, 10, 0.002),
    telemetry: ch(25, 12, 0.004),
    video: {
      ...ch(60, 25, 0.01),
      fps: 30,
      scale: 1,
      freeze_per_min: 1,
      freeze_ms: [150, 500],
      artifacts: 0.05,
    },
  },
  {
    id: 'lte_good',
    name: 'LTE хороший',
    hint: 'Рабочая норма городского маршрута, ~150 мс.',
    command: ch(70, 20, 0.005),
    telemetry: ch(75, 25, 0.01),
    video: {
      ...ch(150, 40, 0.02),
      fps: 25,
      scale: 0.8,
      freeze_per_min: 2,
      freeze_ms: [200, 800],
      artifacts: 0.15,
    },
  },
  {
    id: 'lte_bad',
    name: 'LTE плохой',
    hint: '~400 мс, джиттер, потери, фризы. Основной тренировочный режим.',
    command: ch(200, 90, 0.05, 0.02),
    telemetry: ch(220, 120, 0.08),
    video: {
      ...ch(400, 180, 0.08),
      fps: 10,
      scale: 0.45,
      freeze_per_min: 8,
      freeze_ms: [400, 2200],
      artifacts: 0.5,
    },
  },
  {
    id: 'edge',
    name: 'Граница зоны',
    hint: 'Деградация с обрывами. Здесь проверяется failsafe и восстановление.',
    command: ch(350, 200, 0.15, 0.03),
    telemetry: ch(400, 250, 0.25),
    video: {
      ...ch(700, 300, 0.2),
      fps: 5,
      scale: 0.3,
      freeze_per_min: 18,
      freeze_ms: [600, 3000],
      artifacts: 0.8,
    },
  },
];

export function getPreset(id: string): LinkPreset {
  return LINK_PRESETS.find((p) => p.id === id) ?? LINK_PRESETS[0];
}

/** Глубокая копия пресета: сценарии и ручки в UI правят рабочую копию, не эталон. */
export function clonePreset(p: LinkPreset): LinkPreset {
  return JSON.parse(JSON.stringify(p)) as LinkPreset;
}
